/**
 * EQuIP Dashboard Page
 *
 * Wrapper page for the clinician performance dashboard.
 * Lives in pages/ (not views/equip/) so it is NOT overwritten by update.sh.
 *
 * Adds the shared site chrome — FloatingNav, HeroSection, max-w-7xl container —
 * then renders ProgressDetailsCore from the synced views/equip/ directory.
 *
 * FloatingNav role logic:
 * - Physician lead / admin  → rolePrefix="physician"  (Framework, Leader Guide, FAQ)
 * - APC lead               → rolePrefix="non-physician" (Framework, Leader Guide, FAQ)
 * - equip.read only        → minimal nav (Dashboard + Framework; no physician/APC track pages)
 *
 * Content role behavior mirrors EquipClinicianPage:
 * - equip.read: Shows own data directly (no dropdown in header)
 * - Leadership/Admin: Header shows clinician selector; shows prompt card when none selected
 */

import type { ReactNode } from 'react'
import { User, BarChart3, BookOpen, Calculator, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { FloatingNav } from '@/components/layout/FloatingNav'
import { HeroSection } from '@/components/layout/HeroSection'
import { useRBAC } from '@/hooks/use-rbac'
import { ROLES } from '@/lib/roles'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'
import { useRosterProfile } from '@/hooks/equip/useRosterProfile'
import { ProgressDetailsCore } from '@/views/equip/progress-details/ProgressDetailsCore'

// Nav items for equip.read-only non-physician users (2 items)
const readOnlyNavItems = [
  {
    id: 'performance',
    label: 'My Performance',
    path: '/performance',
    icon: <BarChart3 className="w-4 h-4" />,
  },
  {
    id: 'framework',
    label: 'Framework',
    path: '/framework',
    icon: <BookOpen className="w-4 h-4" />,
  },
]

// Nav items for equip.read-only physician users (3 items — adds My APCs)
const physicianNavItems = [
  { id: 'performance', label: 'My Performance', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'framework', label: 'Framework', path: '/framework', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'apc-dashboard', label: 'My APCs', path: '/apc-dashboard', icon: <Users className="w-4 h-4" /> },
]

export function EquipDashboardPage() {
  const { isAdmin, hasRole } = useRBAC()
  const scope = useEquipScopeOptional()
  const { data: rosterProfile, isLoading: isProfileLoading } = useRosterProfile()

  // Determine track for hero gradient and lead nav
  const navRolePrefix: 'physician' | 'non-physician' | undefined =
    isAdmin || hasRole(ROLES.EQUIP_PHYSICIAN_LEAD)
      ? 'physician'
      : hasRole(ROLES.EQUIP_APC_LEAD) || hasRole(ROLES.EQUIP_SENIOR_LEAD)
        ? 'non-physician'
        : undefined

  const heroRole: 'physician' | 'non-physician' = navRolePrefix ?? 'physician'

  // Detect equip.read-only users (no admin, no lead, no CSS)
  const isReadOnly = !isAdmin && !navRolePrefix && !hasRole(ROLES.EQUIP_CSS)

  // Hide page chrome until roster profile resolves for equip.read users (avoids nav flash)
  if (isReadOnly && isProfileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    )
  }

  // Build nav items per role
  let navItems: Array<{ id: string; label: string; path: string; icon?: ReactNode }>
  let showHome: boolean

  if (isAdmin) {
    navItems = [
      { id: 'framework', label: 'Framework', path: '/framework', icon: <Calculator className="w-4 h-4" /> },
      { id: 'performance', label: 'Performance Dashboard', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
    ]
    showHome = true
  } else if (navRolePrefix) {
    // Lead roles
    navItems = [
      { id: 'framework', label: 'Framework', path: `/${navRolePrefix}/framework`, icon: <Calculator className="w-4 h-4" /> },
      { id: 'leader-guide', label: 'Leader Guide', path: `/${navRolePrefix}/leader-guide`, icon: <BookOpen className="w-4 h-4" /> },
      { id: 'faq', label: 'FAQ', path: `/${navRolePrefix}/faq` },
      { id: 'team', label: 'Team', path: '/leadership', icon: <BarChart3 className="w-4 h-4" /> },
      { id: 'performance', label: 'Performance Dashboard', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
    ]
    showHome = true
  } else if (hasRole(ROLES.EQUIP_CSS)) {
    // CSS staff: dashboard only — no framework, leader guide, or FAQ
    navItems = [
      { id: 'performance', label: 'Performance Dashboard', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
    ]
    showHome = false
  } else {
    // equip.read only — physician gets 3-item nav, APC gets 2-item nav
    navItems = rosterProfile?.isPhysician ? physicianNavItems : readOnlyNavItems
    showHome = false
  }

  // Leadership/admin with a clinician selected → show that clinician's data
  const providerEmail =
    scope?.showClinicianDropdown && scope.selectedClinician
      ? scope.selectedClinician
      : null

  const showSelectPrompt = Boolean(scope?.showClinicianDropdown && !scope.selectedClinician)

  const selectedClinicianName =
    providerEmail && scope?.clinicians
      ? (scope.clinicians.find((c) => c.providerEmail === providerEmail)?.providerFullName ?? null)
      : null

  // Extract first name from "Last, First [MI]." format
  const selectedClinicianFirstName = selectedClinicianName
    ? (selectedClinicianName.split(', ')[1]?.split(' ')[0] ?? null)
    : null

  const heroFirstName = selectedClinicianFirstName ?? rosterProfile?.firstName ?? null

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      <FloatingNav
        items={navItems}
        showHome={showHome}
      />

      <HeroSection
        role={heroRole}
        badgeText="Performance Dashboard"
        title={heroFirstName ? `Hi, ${heroFirstName}!` : 'My EQuIP Performance'}
        description="Track your quality measures, patient outcomes, and incentive award progress."
      />

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6">
          {showSelectPrompt ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <User className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Select a Clinician</h3>
                <p className="text-muted-foreground max-w-md">
                  Use the dropdown in the header to select a clinician and view their
                  individual performance details, patient drill-down, and measure breakdowns.
                </p>
              </CardContent>
            </Card>
          ) : (
            <ProgressDetailsCore providerEmail={providerEmail} embedded={false} />
          )}
        </div>
      </section>
    </div>
  )
}
